import classNames from "classnames";
import { AnimationOptions, useAnimate, useReducedMotion } from "framer-motion";
import { useCollapseChatOnEscape } from "hooks/useCollapseChatOnEscape";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { RootState } from "store/store";

type FrameRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type ChatExpandFrameProps = {
  children: ReactNode;
};

const TRANSITION_DURATION_SECONDS = 0.3;
const TRANSITION_EASE = "easeInOut";

const getViewportRect = (): FrameRect => ({
  top: 0,
  left: 0,
  width: window.innerWidth,
  height: window.innerHeight,
});

const getElementRect = (element: HTMLElement): FrameRect => {
  const { top, left, width, height } = element.getBoundingClientRect();
  return { top, left, width, height };
};

const toAnimationTarget = ({ top, left, width, height }: FrameRect) => ({
  top,
  left,
  width,
  height,
});

const applyRect = (element: HTMLElement, rect: FrameRect) => {
  element.style.top = `${rect.top}px`;
  element.style.left = `${rect.left}px`;
  element.style.width = `${rect.width}px`;
  element.style.height = `${rect.height}px`;
};

const clearRect = (element: HTMLElement) => {
  element.style.removeProperty("top");
  element.style.removeProperty("left");
  element.style.removeProperty("width");
  element.style.removeProperty("height");
};

export const ChatExpandFrame = ({ children }: ChatExpandFrameProps) => {
  const isExpanded = useSelector(
    (state: RootState) => state.chatExpanded.isExpanded
  );
  const [isFloating, setIsFloating] = useState(isExpanded);
  const isFloatingRef = useRef(isExpanded);
  const slotRef = useRef<HTMLDivElement>(null);
  const [frameRef, animate] = useAnimate<HTMLDivElement>();
  const shouldReduceMotion = useReducedMotion();

  useCollapseChatOnEscape();

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const frame = frameRef.current;
    const isAlreadyInTargetState = isExpanded === isFloatingRef.current;

    if (!slot || !frame || isAlreadyInTargetState) return;

    const transition: AnimationOptions = {
      duration: shouldReduceMotion ? 0 : TRANSITION_DURATION_SECONDS,
      ease: TRANSITION_EASE,
    };

    const expand = async () => {
      applyRect(frame, getElementRect(slot));
      isFloatingRef.current = true;
      flushSync(() => setIsFloating(true));

      await animate(frame, toAnimationTarget(getViewportRect()), transition);

      clearRect(frame);
    };

    const collapse = async () => {
      applyRect(frame, getViewportRect());

      await animate(frame, toAnimationTarget(getElementRect(slot)), transition);

      isFloatingRef.current = false;
      flushSync(() => setIsFloating(false));
      clearRect(frame);
    };

    void (isExpanded ? expand() : collapse());
  }, [isExpanded, animate, frameRef, shouldReduceMotion]);

  return (
    <div ref={slotRef} className="t-h-full t-w-full">
      <div
        ref={frameRef}
        className={classNames("t-h-full t-w-full", {
          "t-fixed t-inset-0 t-z-sidebar-arrow t-overflow-hidden t-bg-surface":
            isFloating,
        })}
      >
        {children}
      </div>
    </div>
  );
};
